import { useState, useRef } from 'react';
import { useInterval, useApi } from '../../../utils/hooks';
import { useStoreContext } from '../../../store/store';
import { Helpers, StorageService } from '../../../utils/helpers';
import { IAuthToken } from '../../../utils/constants/interfaces';
import { LogoutReason } from '../../../utils/constants/enums';

const useSessionTimeoutModal = (logout: (reason?: number | undefined) => void) => {
  const tokenCheckInterval = 30; //how often to check token (in seconds - default 30)
  const sessionAlertTimeout = 300; // how many seconds before logout to alert the user (default 300)
  const apiInactivityThreshold = 120; //how many seconds before we consider the user idle (default 120)

  const { user, actions, dispatch } = useStoreContext();
  const api = useApi();
  const [timeUntilLogout, setTimeUntilLogout] = useState(0);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const defaultPageTitle = 'TRGC Membership Application';
  const processInterval = useRef(tokenCheckInterval);
  const isRefreshingToken = useRef(false);

  const refreshSession = async () => {
    //reset page title
    document.title = defaultPageTitle;
    setShowSessionModal(false);
    isRefreshingToken.current = true;
    try {
      const apiResponse = await api.get<string>('/auth/refresh');
      if (apiResponse?.response && apiResponse.status.statusCode === 200) {
        const token: string = apiResponse.response;
        const authToken: IAuthToken = Helpers.decodeAuthToken(token);
        dispatch(actions.setUserAuthToken(authToken));
        StorageService.setAuthToken(token);
        return;
      }
    } catch {
      //ignore
    }
    isRefreshingToken.current = false;
  };

  const checkForAutomaticRenewal = async () => {
    if (!user?.refreshAt) {
      return;
    }
    let timeSinceLastApiActivity: number | null = null;
    if (user.lastApiActivity) {
      timeSinceLastApiActivity = new Date().getTime() - user.lastApiActivity.getTime();
    }

    if (
      Helpers.getUtcDate().getTime() > user.refreshAt.getTime() &&
      timeSinceLastApiActivity &&
      timeSinceLastApiActivity < apiInactivityThreshold * 1000
    ) {
      await refreshSession();
    }
  };

  const handleLogout = () => {
    document.title = defaultPageTitle;
    logout();
  };

  useInterval(() => {
    if (!user?.expiresAt) {
      return;
    }
    const timeRemaining = Math.floor(
      (user.expiresAt.getTime() - Helpers.getUtcDate().getTime()) / 1000
    );
    //set default check interval
    processInterval.current = tokenCheckInterval;

    if (timeRemaining <= 0) {
      document.title = defaultPageTitle;
      logout(LogoutReason.TIMED_OUT);
    } else if (timeRemaining <= sessionAlertTimeout) {
      document.title = `${Helpers.formatTime(timeRemaining)} until logout`;
      setShowSessionModal(!isRefreshingToken.current);
      //increase the interval so the timer display ticks every second
      processInterval.current = 1;
    } else {
      document.title = defaultPageTitle;
      setShowSessionModal(false);
      void checkForAutomaticRenewal();
    }
    setTimeUntilLogout(timeRemaining);
  }, processInterval.current * 1000);

  return {
    timeUntilLogout,
    showSessionModal,
    refreshSession,
    handleLogout
  };
};

export default useSessionTimeoutModal;
